import { executeNextLoop } from '@ifixit/helpers';
import type { LocaleCode } from '@ifixit/i18n/types';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { Sheet, SheetContent, SheetPresence } from '@ifixit/ui/components/sheet';
import { Suspense, lazy, type ElementType } from 'react';
import type { CartImageComponent } from '../types';
import { CartDrawerTrigger } from './CartDrawerTrigger';
import { useCartDrawer } from './hooks/useCartDrawer';

const LazyCartDrawerContent = lazy(() => import('./CartDrawerContent'));

interface CartDrawerProps {
   ImageComponent: CartImageComponent;
   linkComponent?: ElementType;
   supportLinkComponent: ElementType;
   localeCode: LocaleCode;
   viewCartUrl: string;
   storeCode: string;
   showMerchBanner?: boolean;
   recordMerchExperiment: () => boolean;
   /**
    * Link component used by the Pro promo banner when it replaces the merch
    * slot. Separate from `linkComponent` so callers can opt into the upsell
    * without changing the cart's own link rendering.
    */
   promoLinkComponent?: ElementType;
}

export function CartDrawer({
   ImageComponent,
   linkComponent,
   localeCode,
   supportLinkComponent,
   viewCartUrl,
   storeCode,
   recordMerchExperiment,
   promoLinkComponent,
}: CartDrawerProps) {
   const { isOpen, onOpen, onClose } = useCartDrawer();
   const cart = useCart();
   const cartData = cart.data ?? null;

   return (
      <>
         <CartDrawerTrigger
            onClick={() => executeNextLoop(onOpen)}
            hasItemsInCart={cartData?.hasItemsInCart}
         />
         <Sheet
            open={isOpen}
            onOpenChange={open => {
               if (!open) {
                  onClose();
               }
            }}
         >
            {/* The cap flips at Chakra's sm, 576px, which is not Tailwind's
                sm, 640px, so min-[576px] carries the 64px between them.
                sm:max-w-[436px] stays because it is what beats the
                variant's sm:max-w-sm from 640px up: Tailwind orders
                min-width variants by width, so min-[576px] alone would
                lose to it there. pr re-adds the safe-area padding p-0
                swallows. */}
            <SheetContent
               side="right"
               className="w-full max-w-[calc(100vw-60px)] gap-0 bg-white p-0 pr-[env(safe-area-inset-right)] text-base text-gray-800 min-[576px]:max-w-[436px] sm:max-w-[436px]"
               data-mobile-appid="cart-drawer"
               data-testid="cart-drawer"
            >
               <SheetPresence>
                  <Suspense>
                     <LazyCartDrawerContent
                        ImageComponent={ImageComponent}
                        linkComponent={linkComponent}
                        supportLinkComponent={supportLinkComponent}
                        localeCode={localeCode}
                        viewCartUrl={viewCartUrl}
                        storeCode={storeCode}
                        recordMerchExperiment={recordMerchExperiment}
                        promoLinkComponent={promoLinkComponent}
                     />
                  </Suspense>
               </SheetPresence>
            </SheetContent>
         </Sheet>
      </>
   );
}
